import {
    Breadcrumb,
    Layout,
    Form,
    Input,
    DatePicker,
    Select,
    Button,
    Row,
    Col,
    InputNumber,
    Card,
    Switch,
    message,
    Spin,
    Table,
    Typography,
    Modal,
    Space,
    Popconfirm,
    Tabs,
    List
} from 'antd';
import {
    EditOutlined,
    DeleteOutlined,
    PlusOutlined
} from '@ant-design/icons';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import { APPBACKGROUND_COLOR } from '../../appsettings';
import Headerdiv from '../../layout/header';
import Footerdiv from '../../layout/footer';
import axiosClient from '../../../api/axiosClient';

const { Title } = Typography;
const { Content } = Layout;
const { Option } = Select;

export const TrainerEdit = () => {
    const [form] = Form.useForm();
    const [metricsForm] = Form.useForm();
    const { id } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [trainerName, setTrainerName] = useState("");

    // Metrics State
    const [metrics, setMetrics] = useState([]);
    const [metricsLoading, setMetricsLoading] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });
    const [isMetricsModalOpen, setIsMetricsModalOpen] = useState(false);
    const [selectedMetricForEdit, setSelectedMetricForEdit] = useState(null);

    // Member Assignment State
    const [assignedMembers, setAssignedMembers] = useState([]);
    const [membersList, setMembersList] = useState([]);
    const [assignModalOpen, setAssignModalOpen] = useState(false);
    const [selectedMemberId, setSelectedMemberId] = useState(null);
    const [assignmentLoading, setAssignmentLoading] = useState(false);

    // const TOKEN = localStorage.getItem("AUTHCODE"); // Removed
    const USERID = localStorage.getItem("USERID");

    // 1. Fetch Trainer Details
    useEffect(() => {
        const fetchTrainerData = async () => {
            if (!id) return;
            setLoading(true);
            try {
                const response = await axiosClient.get(`/trainer/get_trainer_by_id.php?id=${id}`);
                const data = response.data;

                if (data.status === "success") {
                    const trainer = data.data;
                    setTrainerName(trainer.name);

                    // Populate Form
                    form.setFieldsValue({
                        ...trainer,
                        birthday: trainer.birthday ? dayjs(trainer.birthday) : null,
                        status: trainer.status === true || trainer.status === "1" || trainer.status === 1
                    });
                } else {
                    message.error(data.message || "Trainer not found");
                    navigate("/trainers/view");
                }
            } catch (error) {
                console.error("Fetch Error:", error);
                message.error(error.response?.data?.message || "Error loading trainer details.");
            } finally {
                setLoading(false);
            }
        };

        fetchTrainerData();
    }, [id, form, navigate]);

    // 2. Update Trainer Data (Main Form)
    const onFinish = async (values) => {
        const payload = {
            ...values,
            birthday: values.birthday ? values.birthday.format("YYYY-MM-DD") : null,
            status: values.status ? true : false,
            update_by: USERID,
            target_id: id
        };

        try {
            const response = await axiosClient.post(`/trainer/update_trainer_by_id.php`, payload);

            const data = response.data;

            if (data.status === "success") {
                message.success(data.message);
                navigate("/trainers/view");
            } else {
                message.error(data.message);
            }
        } catch (error) {
            console.error("Error:", error);
            message.error(error.response?.data?.message || "System error. Please try again.");
        }
    };

    // 3. Metrics: Fetch Data
    const fetchMetrics = async (page = 1, pageSize = 10) => {
        if (!id) return;
        setMetricsLoading(true);
        try {
            const response = await axiosClient.get(
                `/trainer/metric/get_all_trainer_metrics_by_id.php?trainer_id=${id}&page=${page}&pageSize=${pageSize}`
            );
            const data = response.data;

            if (data.status === "success") {
                setMetrics(data.data);
                setPagination({
                    current: page,
                    pageSize: pageSize,
                    total: data.total || 0,
                });
            }
        } catch (error) {
            console.error("Metrics fetch error:", error);
            message.error(error.response?.data?.message || "Error loading metrics.");
        } finally {
            setMetricsLoading(false);
        }
    };

    useEffect(() => {
        if (id) fetchMetrics(pagination.current, pagination.pageSize);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const handleTableChange = (newPagination) => {
        fetchMetrics(newPagination.current, newPagination.pageSize);
    };

    // --- Member Assignment Logic ---
    const fetchAssignedMembers = async () => {
        if (!id) return;
        try {
            const response = await axiosClient.get(`/trainer/get_assigned_members.php?trainer_id=${id}`);
            if (response.data.status === "success") {
                setAssignedMembers(response.data.data);
            }
        } catch (error) {
            console.error("Error fetching assigned members:", error);
        }
    };

    const fetchMembersDropdown = async (search = "") => {
        try {
            const response = await axiosClient.get(`/member/get_all_members_dropdown.php?search=${search}`);
            if (response.data.status === "success") {
                setMembersList(response.data.data);
            }
        } catch (error) {
            console.error("Error fetching members:", error);
        }
    };

    useEffect(() => {
        if (id) {
            fetchAssignedMembers();
        }
    }, [id]);

    const handleAssignMember = async () => {
        if (!selectedMemberId) {
            message.error("Please select a member");
            return;
        }
        setAssignmentLoading(true);
        try {
            const response = await axiosClient.post("/member/assign_trainer.php", {
                member_id: selectedMemberId,
                trainer_id: id,
                by: USERID
            });
            if (response.data.status === "success") {
                message.success("Member assigned successfully");
                setAssignModalOpen(false);
                setSelectedMemberId(null);
                fetchAssignedMembers();
            } else {
                message.error(response.data.message);
            }
        } catch (error) {
            message.error(error.response?.data?.message || "Failed to assign member");
        } finally {
            setAssignmentLoading(false);
        }
    };

    const handleRemoveMember = async (memberId) => {
        try {
            const response = await axiosClient.post("/member/remove_trainer.php", {
                member_id: memberId,
                trainer_id: id
            });
            if (response.data.status === "success") {
                message.success("Member removed successfully");
                fetchAssignedMembers();
            } else {
                message.error(response.data.message);
            }
        } catch (error) {
            message.error(error.response?.data?.message || "Failed to remove member");
        }
    };
    // ---------------------------------

    // 4. Metrics: Modal Actions
    const openAddMetricsModal = () => {
        setSelectedMetricForEdit(null);
        metricsForm.resetFields();
        metricsForm.setFieldsValue({ date: dayjs() });
        setIsMetricsModalOpen(true);
    };

    const openEditMetricsModal = (metric) => {
        setSelectedMetricForEdit(metric);
        metricsForm.setFieldsValue({
            ...metric,
            date: metric.date ? dayjs(metric.date) : null,
        });
        setIsMetricsModalOpen(true);
    };

    const calculateBMI = (_, allValues) => {
        const { weight, height } = allValues;
        if (weight && height) {
            const h = height / 100;
            const bmiValue = (weight / (h * h)).toFixed(2);
            metricsForm.setFieldsValue({ bmi: bmiValue });
        }
    };

    const handleMetricsSubmit = async (values) => {
        const basePayload = {
            ...values,
            date: values.date ? values.date.format("YYYY-MM-DD HH:mm:ss") : null,
            trainer_id: id,
        };

        let url = "";
        let finalPayload = {};

        if (selectedMetricForEdit) {
            url = `/trainer/metric/update_trainer_metric_by_id.php`;
            finalPayload = {
                ...basePayload,
                metric_id: selectedMetricForEdit.id,
                updated_by: USERID
            };
        } else {
            url = `/trainer/metric/add_trainer_metric.php`;
            finalPayload = {
                ...basePayload,
                created_by: USERID
            };
        }

        try {
            setLoading(true);
            const response = await axiosClient.post(url, finalPayload);
            const data = response.data;

            if (data.status === "success" || data.status === true) {
                message.success(selectedMetricForEdit ? "Metric updated" : "Metric added");
                setIsMetricsModalOpen(false);
                fetchMetrics(pagination.current, pagination.pageSize);
            } else {
                message.error(data.message || "Operation failed");
            }
        } catch (error) {
            console.error("Metric Save Error:", error);
            message.error(error.response?.data?.message || "System error saving metric");
        } finally {
            setLoading(false);
        }
    };

    // 6. Metrics: Delete
    const deleteMetrics = async (metricId) => {
        try {
            setLoading(true);
            const response = await axiosClient.post(
                `/trainer/metric/delete_trainer_metric_by_id.php`,
                { metric_id: metricId, trainer_id: id, updated_by: USERID }
            );
            const data = response.data;
            if (data.status === true || data.status === "success") {
                message.success("Metric deleted successfully");
                fetchMetrics(pagination.current, pagination.pageSize);
            } else {
                message.error(data.message || "Failed to delete metric");
            }
        } catch (error) {
            console.error("Metrics error:", error);
            message.error(error.response?.data?.message || "Failed to delete metric");
        } finally {
            setLoading(false);
        }
    };

    const metricColumns = [
        {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
            render: (text) => text ? dayjs(text).format("DD MMM YYYY hh:mm A") : "N/A",
        },
        {
            title: 'Weight (kg)',
            dataIndex: 'weight',
            key: 'weight',
        },
        {
            title: 'Height (cm)',
            dataIndex: 'height',
            key: 'height',
        },
        {
            title: 'BMI',
            dataIndex: 'bmi',
            key: 'bmi',
        },
        {
            title: 'Body Fat (%)',
            dataIndex: 'body_fat',
            key: 'body_fat',
        },
        {
            title: 'Muscle Mass (kg)',
            dataIndex: 'muscle_mass',
            key: 'muscle_mass',
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button
                        type="primary"
                        icon={<EditOutlined />}
                        size="small"
                        style={{
                            backgroundColor: "#FFC107",
                            borderColor: "#FFC107",
                            color: "white",
                        }}
                        onClick={() => openEditMetricsModal(record)}
                    />
                    <Popconfirm
                        title="Delete Metric"
                        description="Are you sure to delete this metric?"
                        onConfirm={() => deleteMetrics(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button type="primary" danger icon={<DeleteOutlined />} size="small" />
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <Spin spinning={loading} tip="Processing...">

            <Layout style={{ backgroundColor: APPBACKGROUND_COLOR, minHeight: "100vh" }}>
                <Headerdiv />
                <Content style={{ padding: "10px" }}>
                    <Breadcrumb style={{ marginBottom: 10 }}>
                        <Breadcrumb.Item>
                            <Link to={"/trainers/view"}>Trainers</Link>
                        </Breadcrumb.Item>
                        <Breadcrumb.Item>Edit Trainer</Breadcrumb.Item>
                    </Breadcrumb>

                    <Form layout="vertical" form={form} onFinish={onFinish}>
                        <Card title={`Edit Trainer: ${id || ''}`}>
                            <Row gutter={[16, 16]}>
                                {/* Read Only ID */}
                                <Col xs={24} sm={12} md={4}>
                                    <Form.Item name="trainer_id" label="Trainer ID">
                                        <Input placeholder="ID" disabled />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12} md={4}>
                                    <Form.Item
                                        name="nic"
                                        label="NIC"
                                        rules={
                                            [{ required: true, message: "NIC is required" }, {
                                                pattern: /^([0-9]{9}[VvXx]|[0-9]{12})$/,
                                                message: "Enter a valid Sri Lankan NIC",
                                            }]}
                                    >
                                        <Input placeholder="Enter NIC" />
                                    </Form.Item>
                                </Col>

                                <Col xs={24} sm={12} md={8}>
                                    <Form.Item
                                        name="name"
                                        label="Full Name"
                                        rules={[{ required: true, message: "Name is required" }]}
                                    >
                                        <Input placeholder="Enter Full Name" />
                                    </Form.Item>
                                </Col>

                                <Col xs={12} sm={8} md={4}>
                                    <Form.Item
                                        name="birthday"
                                        label="Birthday"
                                        rules={[{ required: true, message: "Birthday is required" }]}
                                    >
                                        <DatePicker style={{ width: "100%" }} />
                                    </Form.Item>
                                </Col>

                                <Col xs={12} sm={8} md={4}>
                                    <Form.Item
                                        name="gender"
                                        label="Gender"
                                        rules={[{ required: true, message: "Select Gender" }]}
                                    >
                                        <Select placeholder="Select Gender">
                                            <Option value="M">Male</Option>
                                            <Option value="F">Female</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>

                                <Col xs={24} sm={12} md={4}>
                                    <Form.Item
                                        name="contact_no"
                                        label="Contact Number"
                                        rules={[{ required: true, message: "Contact number is required" }]}
                                    >
                                        <Input placeholder="Enter phone number" />
                                    </Form.Item>
                                </Col>

                                <Col xs={24} sm={12} md={4}>
                                    <Form.Item name="emergency_contact" label="Emergency Contact">
                                        <Input placeholder="Enter emergency contact" />
                                    </Form.Item>
                                </Col>

                                <Col xs={16}>
                                    <Form.Item
                                        name="address"
                                        label="Address"
                                        rules={[{ required: true, message: "Address is required" }]}
                                    >
                                        <Input.TextArea rows={1} placeholder="Enter Address" />
                                    </Form.Item>
                                </Col>

                                {/* TRAINER-SPECIFIC */}
                                <Col xs={24} sm={12} md={4}>
                                    <Form.Item
                                        name="specialization"
                                        label="Specialization"
                                        rules={[{ required: true, message: "Required" }]}
                                    >
                                        <Input placeholder="e.g., Yoga" />
                                    </Form.Item>
                                </Col>

                                <Col xs={12} sm={6} md={4}>
                                    <Form.Item
                                        name="experience_years"
                                        label="Experience (Yrs)"
                                    >
                                        <InputNumber min={0} style={{ width: "100%" }} />
                                    </Form.Item>
                                </Col>

                                <Col xs={12} sm={6} md={6}>
                                    <Form.Item name="status" label="Status" valuePropName="checked">
                                        <Switch defaultChecked />
                                    </Form.Item>
                                </Col>

                                <Col xs={24}>
                                    <Form.Item name="health_conditions" label="Health Conditions">
                                        <ReactQuill theme="snow" />
                                    </Form.Item>
                                </Col>

                                {/* MAIN BUTTONS */}
                                <Col xs={24}>
                                    <Row justify="end" gutter={[10, 10]} style={{ marginTop: 10 }}>
                                        <Col>
                                            <Button onClick={() => navigate('/trainers/view')}>Cancel</Button>
                                        </Col>
                                        <Col>
                                            <Button type="primary" htmlType="submit">Update Trainer</Button>
                                        </Col>
                                    </Row>
                                </Col>
                            </Row>

                            {/* Tabs for Metrics and Assignments */}
                            <div style={{ marginTop: "40px" }}>
                                <Tabs
                                    defaultActiveKey="1"
                                    items={[
                                        {
                                            key: '1',
                                            label: 'Performance Metrics',
                                            children: (
                                                <>
                                                    <Row justify="end" style={{ marginBottom: 10 }}>
                                                        <Button
                                                            type="primary"
                                                            icon={<PlusOutlined />}
                                                            onClick={openAddMetricsModal}
                                                        >
                                                            Add Metric
                                                        </Button>
                                                    </Row>
                                                    <Table
                                                        columns={metricColumns}
                                                        dataSource={metrics}
                                                        rowKey="id"
                                                        pagination={pagination}
                                                        loading={metricsLoading}
                                                        onChange={handleTableChange}
                                                        bordered
                                                        size="small"
                                                    />
                                                </>
                                            ),
                                        },
                                        {
                                            key: '2',
                                            label: 'Assigned Members',
                                            children: (
                                                <>
                                                    <Row justify="end" style={{ marginBottom: 10 }}>
                                                        <Button type="primary" onClick={() => {
                                                            fetchMembersDropdown();
                                                            setAssignModalOpen(true);
                                                        }}>
                                                            Assign Member
                                                        </Button>
                                                    </Row>
                                                    <List
                                                        dataSource={assignedMembers}
                                                        locale={{ emptyText: "No members assigned" }}
                                                        renderItem={item => (
                                                            <List.Item
                                                                actions={[
                                                                    <Popconfirm
                                                                        title="Remove Assignment"
                                                                        description="Are you sure to remove this member?"
                                                                        onConfirm={() => handleRemoveMember(item.id)}
                                                                        okText="Yes"
                                                                        cancelText="No"
                                                                    >
                                                                        <Button type="primary" danger icon={<DeleteOutlined />} size="small" />
                                                                    </Popconfirm>
                                                                ]}
                                                            >
                                                                <List.Item.Meta
                                                                    title={item.name}
                                                                    description={
                                                                        <div style={{ fontSize: 12 }}>
                                                                            <div>ID: {item.member_id} | NIC: {item.nic}</div>
                                                                            <div>{item.contact_no}</div>
                                                                        </div>
                                                                    }
                                                                />
                                                            </List.Item>
                                                        )}
                                                    />
                                                </>
                                            ),
                                        },
                                    ]}
                                />
                            </div>
                        </Card>
                    </Form>

                    {/* Metrics Modal */}
                    <Modal
                        title={selectedMetricForEdit ? "Edit Metric" : `Add Metric for ${trainerName}`}
                        open={isMetricsModalOpen}
                        onCancel={() => setIsMetricsModalOpen(false)}
                        footer={null}
                        destroyOnHidden
                    >
                        <Form
                            form={metricsForm}
                            layout="vertical"
                            onFinish={handleMetricsSubmit}
                            onValuesChange={calculateBMI}
                        >
                            <Row gutter={16}>
                                <Col span={24}>
                                    <Form.Item
                                        name="date"
                                        label="Date & Time"
                                        rules={[{ required: true, message: 'Please select date and time' }]}
                                    >
                                        <DatePicker showTime format="YYYY-MM-DD HH:mm:ss" style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item
                                        name="weight"
                                        label="Weight (kg)"
                                        rules={[{ required: true, message: 'Required' }]}
                                    >
                                        <InputNumber placeholder='65kg' style={{ width: '100%' }} min={0} step={0.1} />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="height"
                                        label="Height (cm)"
                                        rules={[{ required: true, message: 'Required' }]}
                                    >
                                        <InputNumber placeholder='172cm' style={{ width: '100%' }} min={0} step={0.1} />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item name="bmi" label="BMI">
                                        <InputNumber style={{ width: '100%' }} readOnly placeholder="Auto-Generated" />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item name="body_fat" label="Body Fat (%)">
                                        <InputNumber placeholder='10%' style={{ width: '100%' }} min={0} step={0.1} />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item name="muscle_mass" label="Muscle Mass (kg)">
                                        <InputNumber placeholder='20' style={{ width: '100%' }} min={0} step={0.1} />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row justify="end">
                                <Space>
                                    <Button onClick={() => setIsMetricsModalOpen(false)}>Cancel</Button>
                                    <Button type="primary" htmlType="submit" loading={loading}>
                                        {selectedMetricForEdit ? "Update" : "Save"}
                                    </Button>
                                </Space>
                            </Row>
                        </Form>
                    </Modal>

                    {/* Assign Member Modal */}
                    <Modal
                        title={`Assign Member to ${trainerName}`}
                        open={assignModalOpen}
                        onCancel={() => setAssignModalOpen(false)}
                        onOk={handleAssignMember}
                        confirmLoading={assignmentLoading}
                    >
                        <p>Select a member to assign:</p>
                        <Select
                            showSearch
                            style={{ width: '100%' }}
                            placeholder="Search and select member"
                            optionFilterProp="children"
                            onChange={(val) => setSelectedMemberId(val)}
                            onSearch={(val) => fetchMembersDropdown(val)}
                            filterOption={false}
                            notFoundContent={null}
                        >
                            {membersList.map(m => (
                                <Option key={m.id} value={m.id}>
                                    {m.name} ({m.member_id})
                                </Option>
                            ))}
                        </Select>
                    </Modal>
                </Content>
                <Footerdiv />
            </Layout>
        </Spin>
    );
};